import { ThemeConfig } from 'antd'
import { theme } from 'antd'

/**
 * Ant Design Theme Configuration
 * 
 * Based on Alio Health Healthcare Design System
 * - Primary: Petronas Green - teal/turquoise inspired
 * - Support both dark and light modes
 * - WCAG AA compliant contrast ratios
 * 
 * @see https://ant.design/docs/react/customize-theme
 * @see https://ant.design/theme-editor
 */

// ========================================
// DARK MODE THEME
// ========================================
export const darkTheme: ThemeConfig = {
  // Use dark algorithm for dark mode
  algorithm: theme.darkAlgorithm,

  // Design tokens
  token: {
    // ========================================
    // Primary Colors (Petronas Green / Teal)
    // ========================================
    colorPrimary: '#00A19B',           // Main teal color
    colorPrimaryHover: '#04BFAD',      // Lighter teal on hover
    colorPrimaryActive: '#027373',     // Darker teal on active
    colorPrimaryBorder: '#00A19B',     // Border color for primary elements

    // ========================================
    // Background Colors (Dark Mode)
    // ========================================
    colorBgContainer: '#1E1E1E',       // Cards, modals, dropdowns
    colorBgElevated: '#0E0F13',        // Elevated surfaces (sidebar, header)
    colorBgLayout: '#0E0F13',          // Main layout background
    colorBgSpotlight: '#2A2A2A',       // Spotlight/hover backgrounds

    // ========================================
    // Text Colors
    // ========================================
    colorText: '#FFFFFF',              // Primary text
    colorTextSecondary: '#C1D4D9',     // Secondary text (descriptions, labels)
    colorTextTertiary: '#979DA6',      // Tertiary text (disabled, placeholders)
    colorTextQuaternary: '#565F64',    // Quaternary text (subtle hints)

    // ========================================
    // Border Colors
    // ========================================
    colorBorder: '#C8CCCE',            // Default borders
    colorBorderSecondary: '#565F64',   // Secondary borders (dividers)

    // ========================================
    // Semantic Colors
    // ========================================
    colorSuccess: '#04BFAD',           // Success states (teal variant)
    colorWarning: '#FFC107',           // Warning states (amber)
    colorError: '#E53935',             // Error states (red)
    colorInfo: '#027373',              // Info states (darker teal)

    // ========================================
    // Typography
    // ========================================
    fontSize: 16,                      // Base font size
    fontSizeHeading1: 38,              // H1
    fontSizeHeading2: 30,              // H2
    fontSizeHeading3: 24,              // H3
    fontSizeHeading4: 20,              // H4
    fontSizeHeading5: 16,              // H5
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

    // ========================================
    // Border Radius
    // ========================================
    borderRadius: 12,                  // Default border radius
    borderRadiusLG: 16,                // Large elements
    borderRadiusSM: 8,                 // Small elements
    borderRadiusXS: 4,                 // Extra small elements

    // ========================================
    // Spacing
    // ========================================
    controlHeight: 40,                 // Default control height (buttons, inputs)
    controlHeightLG: 48,               // Large controls
    controlHeightSM: 32,               // Small controls

    // ========================================
    // Effects
    // ========================================
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    boxShadowSecondary: '0 4px 16px rgba(0, 0, 0, 0.2)',
  },

  // ========================================
  // Component-specific overrides
  // ========================================
  components: {
    Button: {
      primaryColor: '#FFFFFF',         // Text color for primary button
      defaultBorderColor: '#C8CCCE',   // Border for default button
      defaultColor: '#FFFFFF',         // Text color for default button
      controlHeight: 44,               // Button height
      controlHeightLG: 48,             // Large button height
      controlHeightSM: 36,             // Small button height
      borderRadius: 12,                // Rounded corners
      fontWeight: 600,                 // Semi-bold text
      boxShadow: 'none',               // Remove shadow
      primaryShadow: 'none',           // Remove primary button shadow
      defaultShadow: 'none',           // Remove default button shadow
      dangerShadow: 'none',            // Remove danger button shadow
      controlOutline: 'none',          // Remove outline
      controlOutlineWidth: 0,          // Set outline width to 0
    },

    Input: {
      controlHeight: 44,               // Input height
      controlHeightLG: 48,             // Large input
      controlHeightSM: 36,             // Small input
      borderRadius: 12,                // Rounded corners
      colorBorder: '#C8CCCE',          // Border color (default state)
      colorTextPlaceholder: '#979DA6', // Placeholder text
      colorBgContainer: '#FFFFFF',     // White background for input
      colorText: '#000000',            // Black text for readability on white
      activeBorderColor: '#04BFAD',    // Primary color when focused
      hoverBorderColor: '#04BFAD',     // Lighter teal on hover
      activeShadow: 'none',            // Remove focus shadow/outline (prevents "thick border" effect)
      controlOutline: 'none',          // Remove outline
      controlOutlineWidth: 0,          // Set outline width to 0
    },

    Select: {
      controlHeight: 44,
      borderRadius: 12,
    },

    DatePicker: {
      controlHeight: 44,
      borderRadius: 12,
    },

    Card: {
      borderRadiusLG: 16,
      paddingLG: 24,
    },

    Modal: {
      borderRadiusLG: 16,
      padding: 24,
    },

    Table: {
      borderRadius: 12,
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },

    Menu: {
      itemBorderRadius: 8,
      itemSelectedBg: 'rgba(0, 161, 155, 0.15)', // Teal with opacity
      itemSelectedColor: '#04BFAD',              // Lighter teal for selected
    },

    Message: {
      contentBg: '#1E1E1E',
      borderRadiusLG: 12,
    },

    Notification: {
      borderRadiusLG: 12,
    },

    Tabs: {
      itemSelectedColor: '#04BFAD',    // Selected tab color
      itemHoverColor: '#00A19B',       // Hover color
      inkBarColor: '#04BFAD',          // Active indicator
    },

    Badge: {
      colorBorderBg: '#0E0F13',        // Badge background border
    },

    Pagination: {
      itemActiveBg: '#00A19B',         // Active page background
      borderRadius: 8,
    },
  },
}

// ========================================
// LIGHT MODE THEME
// ========================================
export const lightTheme: ThemeConfig = {
  // Use default algorithm for light mode
  algorithm: theme.defaultAlgorithm,

  // Design tokens
  token: {
    // ========================================
    // Primary Colors (Petronas Green / Teal)
    // ========================================
    colorPrimary: '#009C92',           // Main teal color (slightly darker for light mode)
    colorPrimaryHover: '#00B6AB',      // Lighter teal on hover
    colorPrimaryActive: '#027373',     // Darker teal on active
    colorPrimaryBorder: '#009C92',     // Border color for primary elements

    // ========================================
    // Background Colors (Light Mode)
    // ========================================
    colorBgContainer: '#FFFFFF',       // Cards, modals, dropdowns - pure white
    colorBgElevated: '#F5F6F7',        // Elevated surfaces (sidebar, header)
    colorBgLayout: '#F5F6F7',          // Main layout background
    colorBgSpotlight: '#E9ECEF',       // Spotlight/hover backgrounds

    // ========================================
    // Text Colors
    // ========================================
    colorText: '#0E0F13',              // Primary text (dark for readability)
    colorTextSecondary: '#5E6B70',     // Secondary text (descriptions, labels)
    colorTextTertiary: '#6E7B83',      // Tertiary text (disabled, placeholders)
    colorTextQuaternary: '#C8CCCE',    // Quaternary text (subtle hints)

    // ========================================
    // Border Colors
    // ========================================
    colorBorder: '#E9ECEF',            // Default borders
    colorBorderSecondary: '#F1F3F5',   // Secondary borders (dividers)

    // ========================================
    // Semantic Colors
    // ========================================
    colorSuccess: '#00BFA6',           // Success states (teal variant)
    colorWarning: '#FFC107',           // Warning states (amber)
    colorError: '#D32F2F',             // Error states (red)
    colorInfo: '#027373',              // Info states (darker teal)

    // ========================================
    // Typography
    // ========================================
    fontSize: 16,                      // Base font size
    fontSizeHeading1: 38,              // H1
    fontSizeHeading2: 30,              // H2
    fontSizeHeading3: 24,              // H3
    fontSizeHeading4: 20,              // H4
    fontSizeHeading5: 16,              // H5
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

    // ========================================
    // Border Radius
    // ========================================
    borderRadius: 12,                  // Default border radius
    borderRadiusLG: 16,                // Large elements
    borderRadiusSM: 8,                 // Small elements
    borderRadiusXS: 4,                 // Extra small elements

    // ========================================
    // Spacing
    // ========================================
    controlHeight: 40,                 // Default control height (buttons, inputs)
    controlHeightLG: 48,               // Large controls
    controlHeightSM: 32,               // Small controls

    // ========================================
    // Effects
    // ========================================
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    boxShadowSecondary: '0 4px 16px rgba(0, 0, 0, 0.12)',
  },

  // ========================================
  // Component-specific overrides
  // ========================================
  components: {
    Button: {
      primaryColor: '#FFFFFF',         // Text color for primary button
      defaultBorderColor: '#E9ECEF',   // Border for default button
      defaultColor: '#0E0F13',         // Text color for default button
      controlHeight: 44,               // Button height
      controlHeightLG: 48,             // Large button height
      controlHeightSM: 36,             // Small button height
      borderRadius: 12,                // Rounded corners
      fontWeight: 600,                 // Semi-bold text
      boxShadow: 'none',               // Remove shadow
      primaryShadow: 'none',           // Remove primary button shadow
      defaultShadow: 'none',           // Remove default button shadow
      dangerShadow: 'none',            // Remove danger button shadow
      controlOutline: 'none',          // Remove outline
      controlOutlineWidth: 0,          // Set outline width to 0
    },

    Input: {
      controlHeight: 44,               // Input height
      controlHeightLG: 48,             // Large input
      controlHeightSM: 36,             // Small input
      borderRadius: 12,                // Rounded corners
      colorBorder: '#E9ECEF',          // Border color (default state)
      colorTextPlaceholder: '#6E7B83', // Placeholder text
      colorBgContainer: '#FFFFFF',     // White background for input
      colorText: '#0E0F13',            // Dark text for readability
      activeBorderColor: '#009C92',    // Primary color when focused
      hoverBorderColor: '#00B6AB',     // Lighter teal on hover
      activeShadow: 'none',            // Remove focus shadow/outline
      controlOutline: 'none',          // Remove outline
      controlOutlineWidth: 0,          // Set outline width to 0
    },

    Select: {
      controlHeight: 44,
      borderRadius: 12,
    },

    DatePicker: {
      controlHeight: 44,
      borderRadius: 12,
    },

    Card: {
      borderRadiusLG: 16,
      paddingLG: 24,
    },

    Modal: {
      borderRadiusLG: 16,
      padding: 24,
    },

    Table: {
      borderRadius: 12,
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },

    Menu: {
      itemBorderRadius: 8,
      itemSelectedBg: 'rgba(0, 156, 146, 0.1)', // Teal with opacity
      itemSelectedColor: '#009C92',             // Primary teal for selected
    },

    Message: {
      contentBg: '#FFFFFF',
      borderRadiusLG: 12,
    },

    Notification: {
      borderRadiusLG: 12,
    },

    Tabs: {
      itemSelectedColor: '#009C92',    // Selected tab color
      itemHoverColor: '#00B6AB',       // Hover color
      inkBarColor: '#009C92',          // Active indicator
    },

    Badge: {
      colorBorderBg: '#F5F6F7',        // Badge background border
    },

    Pagination: {
      itemActiveBg: '#009C92',         // Active page background
      borderRadius: 8,
    },
  },
}

// Default export for backward compatibility (defaults to dark theme)
export default darkTheme

// Helper function to get theme by mode
export const getTheme = (mode: 'light' | 'dark'): ThemeConfig => {
  return mode === 'light' ? lightTheme : darkTheme
}
